import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createIndustrialCostDirectMaterialEntry,
  deleteIndustrialCostDirectMaterialEntry,
  getIndustrialCostDirectMaterialEntries,
  getIndustrialCostDirectMaterials,
  updateIndustrialCostDirectMaterialEntry,
} from '../services/industrialCostService';
import {
  applyDirectInputsImportMapping,
  applyDirectInputsMaterialResolution,
  canContinueDirectInputsImport,
  confirmDirectInputsImport,
  createDirectInputsImportFlowState,
  getDirectInputsUnresolvedMaterialRefs,
  runDirectInputsImportPreview,
} from '../importFlows/directInputsImportFlow';
import {
  analyzeFileNormalization,
  commitFileNormalization,
  reviewFileNormalization,
} from '../fileNormalization/service';
import FileNormalizationReviewPanel from '../fileNormalization/components/FileNormalizationReviewPanel';
import type { FileNormalizationAnalyzeResult } from '../fileNormalization/types';
import type {
  CreateDirectMaterialEntryPayload,
  DirectMaterial,
  DirectMaterialEntry,
  UpdateDirectMaterialEntryPayload,
} from '../types/industrialCost';
import { IMPORT_PERMISSION_DENIED_TEXT, useCanManageImports } from '../utils/importPermission';

import { formatEsNumber } from '../utils/numberFormat';
type FormState = {
  material_id: string;
  entry_date: string;
  quantity: string;
  unit_price: string;
  supplier: string;
  document_ref: string;
  source_reference: string;
  notes: string;
  is_active: boolean;
};

const todayIso = new Date().toISOString().slice(0, 10);

const EMPTY_FORM: FormState = {
  material_id: '',
  entry_date: todayIso,
  quantity: '',
  unit_price: '',
  supplier: '',
  document_ref: '',
  source_reference: '',
  notes: '',
  is_active: true,
};

const MONTH_OPTIONS = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const directMaterialEntryImportFields = [
  { key: 'material', label: 'Material', required: true },
  { key: 'entry_date', label: 'Fecha entrada', required: true },
  { key: 'quantity', label: 'Cantidad', required: true },
  { key: 'unit_price', label: 'Precio unitario', required: true },
  { key: 'supplier', label: 'Proveedor', required: false },
  { key: 'document_ref', label: 'Documento', required: false },
  { key: 'source_reference', label: 'Referencia origen', required: false },
  { key: 'notes', label: 'Notas', required: false },
  { key: 'is_active', label: 'Activo', required: false },
] as const;

function buildPayload(form: FormState): CreateDirectMaterialEntryPayload {
  return {
    material_id: form.material_id,
    entry_date: form.entry_date,
    quantity: Number(form.quantity),
    unit_price: Number(form.unit_price),
    supplier: form.supplier.trim() || null,
    document_ref: form.document_ref.trim() || null,
    source_reference: form.source_reference.trim() || null,
    notes: form.notes.trim() || null,
    is_active: form.is_active,
  };
}

function formatDisplayValue(value: unknown, fallback = '—'): string {
  if (value === null || value === undefined) return fallback;

  if (typeof value === 'number' && Number.isNaN(value)) {
    return fallback;
  }

  const text = String(value).trim();

  if (!text || ['nan', 'none', 'null', 'undefined'].includes(text.toLowerCase())) {
    return fallback;
  }

  return text;
}

type IndustrialCostDirectInputsViewProps = {
  importOnly?: boolean;
  initialImportFile?: File | null;
  onImportClose?: () => void;
  onImportSuccess?: (message: string) => void;
};

export default function IndustrialCostDirectInputsView({
  importOnly = false,
  initialImportFile = null,
  onImportClose,
  onImportSuccess,
}: IndustrialCostDirectInputsViewProps = {}) {
  const [materials, setMaterials] = useState<DirectMaterial[]>([]);
  const [entries, setEntries] = useState<DirectMaterialEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [monthFilter, setMonthFilter] = useState<'all' | string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesPage, setEntriesPage] = useState(0);
  const [entriesRowsPerPage, setEntriesRowsPerPage] = useState(25);

  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const importInputRef = useRef<HTMLInputElement | null>(null);

  const [importDialogOpen, setImportDialogOpen] = useState(
    importOnly && initialImportFile !== null,
  );

  const [importStep, setImportStep] = useState<1 | 2>(1);

  const [importFlow, setImportFlow] = useState(
    createDirectInputsImportFlowState(
      importOnly ? initialImportFile : null,
    ),
  );
  const [normalizationResult, setNormalizationResult] =
    useState<FileNormalizationAnalyzeResult | null>(null);
  const [normalizationReviewSheetName, setNormalizationReviewSheetName] =
    useState('');
  const [normalizationReviewHeaderRowInput, setNormalizationReviewHeaderRowInput] =
    useState('1');
  const [reviewingNormalization, setReviewingNormalization] = useState(false);
  const [savingNormalizationProfile, setSavingNormalizationProfile] =
    useState(false);

  const importFile = importFlow.file;
  const importPreview = importFlow.preview;
  const importMapping = importFlow.mapping;
  const materialResolution = importFlow.materialResolution;
  const previewingImport = importFlow.status === 'loading';
  const confirmingImport = importFlow.status === 'confirming';
  const importError = importFlow.error;
  const importResultMessage = importFlow.resultMessage;

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      const [materialsData, entriesData] = await Promise.all([
        getIndustrialCostDirectMaterials(),
        importOnly
          ? Promise.resolve([] as DirectMaterialEntry[])
          : getIndustrialCostDirectMaterialEntries(),
      ]);

      setMaterials(materialsData);
      setEntries(entriesData);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudieron cargar las entradas de materiales.',
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  const materialsById = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])),
    [materials],
  );

  const activeMaterials = useMemo(
    () => materials.filter((m) => m.is_active),
    [materials],
  );

  const filteredEntries = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return entries.filter((entry) => {
      const material = materialsById[entry.material_id];
      const entryDate = new Date(`${entry.entry_date}T00:00:00`);
      const entryYear = entry.entry_date.slice(0, 4);
      const entryMonth = String(entryDate.getMonth() + 1);

      const matchesSearch =
        !normalizedSearch ||
        [
          material?.code,
          material?.name,
          entry.entry_date,
          entry.quantity,
          entry.unit_price,
          entry.supplier,
          entry.document_ref,
          entry.source_reference,
          entry.notes,
          entry.is_active ? 'activa' : 'inactiva',
        ]
          .filter((value) => value !== null && value !== undefined)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      const matchesYear = !yearFilter || entryYear === yearFilter;
      const matchesMonth = monthFilter === 'all' || entryMonth === monthFilter;

      return matchesSearch && matchesYear && matchesMonth;
    });
  }, [entries, materialsById, monthFilter, searchQuery, yearFilter]);

  useEffect(() => {
    setEntriesPage(0);
  }, [searchQuery, yearFilter, monthFilter, entries.length]);

  const paginatedEntries = useMemo(() => {
    const start = entriesPage * entriesRowsPerPage;
    return filteredEntries.slice(start, start + entriesRowsPerPage);
  }, [entriesPage, entriesRowsPerPage, filteredEntries]);

  const activeEntryMaterialIds = new Set(
    entries.filter((entry) => entry.is_active).map((entry) => entry.material_id),
  );

  const materialsWithoutEntries = activeMaterials.filter(
    (material) => !activeEntryMaterialIds.has(material.id),
  );

  const activeEntriesCount = entries.filter((entry) => entry.is_active).length;
  const materialsWithEntriesCount = activeEntryMaterialIds.size;
  const suppliersCount = new Set(
    entries
      .map((entry) => entry.supplier?.trim())
      .filter((supplier): supplier is string => Boolean(supplier)),
  ).size;
  const totalEntriesValue = entries
    .filter((entry) => entry.is_active)
    .reduce((sum, entry) => sum + Number(entry.quantity || 0) * Number(entry.unit_price || 0), 0);

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function openCreateEntryDialog() {
    resetForm();
    setError(null);
    setSuccessMessage(null);
    setEntryDialogOpen(true);
  }

  function closeEntryDialog() {
    if (saving) return;

    setEntryDialogOpen(false);
    resetForm();
  }

  function startEdit(entry: DirectMaterialEntry) {
    setEditingId(entry.id);
    setForm({
      material_id: entry.material_id,
      entry_date: entry.entry_date,
      quantity: String(entry.quantity),
      unit_price: String(entry.unit_price),
      supplier: entry.supplier ?? '',
      document_ref: entry.document_ref ?? '',
      source_reference: entry.source_reference ?? '',
      notes: entry.notes ?? '',
      is_active: entry.is_active,
    });
    setError(null);
    setSuccessMessage(null);
    setEntryDialogOpen(true);
  }

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = buildPayload(form);

      if (!payload.material_id) {
        throw new Error('Debes seleccionar un material.');
      }
      if (!payload.entry_date) {
        throw new Error('Debes informar la fecha de entrada.');
      }
      // IND-014: como la importación: cantidad ≠ 0 (negativa = devolución)
      // y precio ≥ 0.
      if (String(form.quantity ?? '').trim() === '' || !Number.isFinite(payload.quantity)) {
        throw new Error('Informa una cantidad numérica (negativa para una devolución).');
      }
      if (payload.quantity === 0) {
        throw new Error('La cantidad no puede ser 0 (usa un número negativo para una devolución).');
      }
      if (String(form.unit_price ?? '').trim() === '') {
        throw new Error('Informa el precio unitario (0 si la entrega no tiene precio).');
      }
      if (!Number.isFinite(payload.unit_price) || payload.unit_price < 0) {
        throw new Error('El precio unitario no puede ser negativo.');
      }

      if (editingId) {
        await updateIndustrialCostDirectMaterialEntry(
          editingId,
          payload as UpdateDirectMaterialEntryPayload,
        );
        setSuccessMessage('Entrada actualizada correctamente.');
      } else {
        await createIndustrialCostDirectMaterialEntry(payload);
        setSuccessMessage('Entrada creada correctamente.');
      }

      setEntryDialogOpen(false);
      resetForm();
      await loadData();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo guardar la entrada de material.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(entryId: string) {
    const confirmed = window.confirm(
      '¿Seguro que quieres eliminar esta entrada de material?',
    );
    if (!confirmed) return;

    setError(null);
    setSuccessMessage(null);

    try {
      await deleteIndustrialCostDirectMaterialEntry(entryId);
      if (editingId === entryId) {
        resetForm();
      }
      setSuccessMessage('Entrada eliminada correctamente.');
      await loadData();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo eliminar la entrada de material.',
      );
    }
  }

  async function runImportPreview(
    file: File,
    mapping?: Record<string, string | null>,
    nextMaterialResolution?: Record<string, string | null>,
    normalization?: {
      sheet_name?: string | null;
      header_row_index?: number | null;
    } | null,
  ) {
    setError(null);
    setSuccessMessage(null);
    setImportFlow((prev) => ({
      ...prev,
      file,
      mapping: mapping ?? prev.mapping,
      materialResolution: nextMaterialResolution ?? prev.materialResolution,
      normalization: normalization ?? prev.normalization,
      status: 'loading',
      error: null,
      resultMessage: null,
    }));

    const nextState = await runDirectInputsImportPreview(
      createDirectInputsImportFlowState(file),
      {
        file,
        mapping,
        materialResolution: nextMaterialResolution,
        normalization,
      },
    );

    setImportFlow(nextState);
    setImportDialogOpen(true);
  }

  // IND-007
  const canImport = useCanManageImports();

  function handleOpenImportPicker() {
    if (!canImport) return;
    importInputRef.current?.click();
  }

  async function handleNormalizationSheetChange(
    sheetName: string,
  ) {
    if (!importFile || !sheetName) {
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      setReviewingNormalization(true);

      const reviewedNormalization =
        await reviewFileNormalization(
          importFile,
          'direct_material_entries',
          0,
          sheetName,
        );

      setNormalizationResult(reviewedNormalization);
      setNormalizationReviewSheetName(
        reviewedNormalization.selected_sheet ?? '',
      );
      setNormalizationReviewHeaderRowInput(
        String(
          reviewedNormalization.header_row_index + 1,
        ),
      );

      setImportFlow(
        createDirectInputsImportFlowState(importFile),
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo cargar la muestra de la hoja seleccionada.',
      );
    } finally {
      setReviewingNormalization(false);
    }
  }

  async function persistDirectInputsNormalizationProfile(
    normalization: FileNormalizationAnalyzeResult,
    mapping: Record<string, string | null>,
    knownColumns: string[],
  ) {
    return commitFileNormalization({
      importer_key: 'direct_material_entries',
      sheet_name: normalization.selected_sheet,
      header_row_index: normalization.header_row_index,
      data_start_row_index:
        normalization.header_row_index + 1,
      column_mapping: Object.fromEntries(
        Object.entries(mapping).filter(
          (
            entry,
          ): entry is [string, string] =>
            typeof entry[1] === 'string' &&
            entry[1].trim().length > 0,
        ),
      ),
      known_columns: knownColumns,
    });
  }

  async function handleUseNormalization() {
    if (!importFile || !normalizationResult) {
      return;
    }

    const parsedHeaderRow = Number(
      normalizationReviewHeaderRowInput,
    );

    if (
      !Number.isInteger(parsedHeaderRow) ||
      parsedHeaderRow < 1
    ) {
      setError(
        'Selecciona visualmente una fila válida como cabecera.',
      );
      return;
    }

    const selectedSheet =
      normalizationReviewSheetName ||
      normalizationResult.selected_sheet ||
      null;

    const selectedHeaderRowIndex =
      parsedHeaderRow - 1;

    try {
      setError(null);
      setSuccessMessage(null);
      setSavingNormalizationProfile(true);

      let finalNormalization =
        normalizationResult;

      const structureChanged =
        selectedSheet !==
          (normalizationResult.selected_sheet ?? null) ||
        selectedHeaderRowIndex !==
          normalizationResult.header_row_index;

      if (structureChanged) {
        setReviewingNormalization(true);

        finalNormalization =
          await reviewFileNormalization(
            importFile,
            'direct_material_entries',
            selectedHeaderRowIndex,
            selectedSheet,
          );

        setNormalizationResult(finalNormalization);
        setNormalizationReviewSheetName(
          finalNormalization.selected_sheet ?? '',
        );
        setNormalizationReviewHeaderRowInput(
          String(
            finalNormalization.header_row_index + 1,
          ),
        );
      }

      await persistDirectInputsNormalizationProfile(
        finalNormalization,
        finalNormalization.applied_column_mapping ?? {},
        finalNormalization.detected_columns,
      );

      setImportStep(1);

      await runImportPreview(
        importFile,
        finalNormalization.applied_column_mapping ??
          undefined,
        undefined,
        {
          sheet_name:
            finalNormalization.selected_sheet,
          header_row_index:
            finalNormalization.header_row_index,
        },
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo guardar la estructura seleccionada.',
      );
    } finally {
      setSavingNormalizationProfile(false);
      setReviewingNormalization(false);
    }
  }

  async function handleSelectImportFile(file: File | null) {
    if (!file) return;

    setImportDialogOpen(true);
    setImportStep(1);
    setNormalizationResult(null);
    setImportFlow(createDirectInputsImportFlowState(file));

    try {
      const normalization = await analyzeFileNormalization(
        file,
        'direct_material_entries',
      );

      setNormalizationResult(normalization);
      setNormalizationReviewSheetName(
        normalization.selected_sheet ?? '',
      );
      setNormalizationReviewHeaderRowInput(
        String(normalization.header_row_index + 1),
      );
    } catch (err: any) {
      setImportDialogOpen(importOnly);
      setNormalizationResult(null);
      setNormalizationReviewSheetName('');
      setNormalizationReviewHeaderRowInput('1');
      setImportFlow(createDirectInputsImportFlowState());

      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo analizar el archivo antes de generar la previsualización.',
      );
    }
  }

  useEffect(() => {
    if (importOnly && initialImportFile) {
      void handleSelectImportFile(initialImportFile);
    }
  }, [importOnly, initialImportFile]);

  function handleCloseImportDialog() {
    if (
      confirmingImport ||
      reviewingNormalization ||
      savingNormalizationProfile
    ) {
      return;
    }

    setImportDialogOpen(false);
    setImportStep(1);
    setNormalizationResult(null);
    setNormalizationReviewSheetName('');
    setNormalizationReviewHeaderRowInput('1');
    setImportFlow(createDirectInputsImportFlowState());

    if (importInputRef.current) {
      importInputRef.current.value = '';
    }

    if (importOnly) {
      onImportClose?.();
    }
  }

  async function handleChangeImportMapping(field: string, column: string) {
    if (!importFile) return;

    setImportFlow((prev) => ({
      ...prev,
      status: 'loading',
      error: null,
      resultMessage: null,
    }));

    const nextState = await applyDirectInputsImportMapping(importFlow, field, column);
    setImportFlow(nextState);
  }

  async function handleContinueImportMapping() {
    if (
      !normalizationResult ||
      !importPreview ||
      !canContinueImport
    ) {
      return;
    }

    try {
      setError(null);
      setSavingNormalizationProfile(true);

      await persistDirectInputsNormalizationProfile(
        normalizationResult,
        importFlow.mapping,
        normalizationResult.detected_columns.length > 0
          ? normalizationResult.detected_columns
          : importPreview.columns,
      );

      setImportStep(2);
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        'No se pudo guardar el mapeo de columnas.';

      setError(message);
      setImportFlow((prev) => ({
        ...prev,
        error: message,
      }));
    } finally {
      setSavingNormalizationProfile(false);
    }
  }

  async function handleChangeMaterialResolution(sourceValue: string, materialId: string) {
    if (!importFile) return;

    setImportFlow((prev) => ({
      ...prev,
      status: 'loading',
      error: null,
      resultMessage: null,
    }));

    const nextState = await applyDirectInputsMaterialResolution(
      importFlow,
      sourceValue,
      materialId,
    );
    setImportFlow(nextState);
  }

  async function handleConfirmImport() {
    if (!importFile || !importPreview) return;

    setImportFlow((prev) => ({
      ...prev,
      status: 'confirming',
      error: null,
      resultMessage: null,
    }));

    const nextState = await confirmDirectInputsImport(importFlow);
    setImportFlow(nextState);

    if (
      nextState.status === 'imported' ||
      nextState.status === 'imported_with_issues'
    ) {
      const nextResultMessage =
        nextState.resultMessage ?? 'Importación completada.';

      if (importOnly) {
        onImportSuccess?.(nextResultMessage);
        return;
      }

      handleCloseImportDialog();

      setImportFlow((prev) => ({
        ...prev,
        resultMessage: nextResultMessage,
      }));

      await loadData();
    }
  }

  const canContinueImport = canContinueDirectInputsImport(importFlow);

  const unresolvedMaterialRefs = getDirectInputsUnresolvedMaterialRefs(importFlow);

  return (
    <Stack spacing={3}>
      {!importOnly && (
        <>
          <input
            ref={importInputRef}
        type="file"
        accept=".csv,.xlsx"
        hidden
        onChange={(event) => handleSelectImportFile(event.target.files?.[0] ?? null)}
      />
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={1.25}>
          <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
            Entradas / compras de materiales
          </Typography>

          <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
            Base económica de los materiales directos. Aquí se registran entradas reales
            con fecha, cantidad y precio unitario para alimentar la valoración PMP.
          </Typography>

          <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
            Esta vista no calcula el coste final por sí sola. Deja preparada la fuente de precio
            que después se combina con inventario y consumos técnicos.
          </Typography>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(260px, 1fr)' },
            gap: 2.5,
            alignItems: 'stretch',
          }}
        >
          <Stack spacing={2.25}>
            <Box>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                Cobertura de entradas
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', mt: 0.5 }}>
                Estado de la base de compras disponible para valorar materiales.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              {[
                { label: 'Entradas totales', value: entries.length },
                { label: 'Entradas activas', value: activeEntriesCount },
                { label: 'Materiales con entradas', value: materialsWithEntriesCount },
                { label: 'Materiales sin entradas', value: materialsWithoutEntries.length },
                { label: 'Proveedores', value: suppliersCount },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(2,6,23,0.32)',
                    px: 1.75,
                    py: 1.35,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.35rem' }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Box
                sx={{
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.72)',
                  color: '#cbd5e1',
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.82rem',
                  fontWeight: 800,
                }}
              >
                Valor total activo · {formatEsNumber(totalEntriesValue, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Box>
            </Box>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(34,211,238,0.18)',
              bgcolor: 'rgba(8,47,73,0.16)',
              p: 2,
              alignSelf: 'stretch',
            }}
          >
            <Stack spacing={1.5} sx={{ height: '100%' }}>
              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Acciones
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', mt: 0.35 }}>
                  Alta manual o carga masiva de entradas.
                </Typography>
              </Box>

              <Stack spacing={1} sx={{ mt: 'auto' }}>
                {!canImport ? (
                  <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>
                    {IMPORT_PERMISSION_DENIED_TEXT}
                  </Typography>
                ) : null}
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenImportPicker}
                  disabled={previewingImport || !canImport}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  {previewingImport ? 'Leyendo archivo...' : 'Importar entradas'}
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={openCreateEntryDialog}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: '#38bdf8',
                    color: '#0f172a',
                    '&:hover': {
                      bgcolor: '#38bdf8',
                      filter: 'brightness(1.06)',
                    },
                  }}
                >
                  Nueva entrada
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {importResultMessage ? (
        <Alert
          severity="success"
          onClose={() =>
            setImportFlow((prev) => ({
              ...prev,
              resultMessage: null,
            }))
          }
        >
          {importResultMessage}
        </Alert>
      ) : null}

      {!loading && activeMaterials.length === 0 ? (
        <Alert severity="warning">
          No hay materiales directos activos. Antes de registrar entradas debes
          dar de alta el catálogo maestro de materiales.
        </Alert>
      ) : null}

      {!loading &&
      activeMaterials.length > 0 &&
      materialsWithoutEntries.length > 0 ? (
        <Alert severity="warning">
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 800 }}>
              Hay <strong>{materialsWithoutEntries.length}</strong> materiales activos sin entrada registrada.
            </Typography>

            <Typography variant="body2">
              Mientras falten entradas válidas, esos materiales no podrán resolver automáticamente
              un último precio disponible. Puedes cargar una entrada, informar una valoración manual
              o desactivar el material si no aplica.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {materialsWithoutEntries.slice(0, 8).map((material) => (
                <Chip
                  key={material.id}
                  size="small"
                  variant="outlined"
                  label={`${material.code} · ${material.name}`}
                />
              ))}

              {materialsWithoutEntries.length > 8 ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`+${materialsWithoutEntries.length - 8} más`}
                />
              ) : null}
            </Box>
          </Stack>
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
              <Stack spacing={0.5}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '1rem' }}>
                  Histórico de entradas
                </Typography>
                <Typography sx={{ color: '#94a3b8' }}>
                  Entradas disponibles para valoración de materiales y cálculo PMP.
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'minmax(280px, 1.4fr) minmax(120px, 0.45fr) minmax(180px, 0.7fr)',
                  },
                  gap: 1.25,
                  minWidth: { xs: '100%', md: 620 },
                }}
              >
                <TextField
                  size="small"
                  label="Buscar"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Material, proveedor, documento, estado..."
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Año"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  fullWidth
                />

                <TextField
                  select
                  size="small"
                  label="Mes"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value as 'all' | string)}
                  fullWidth
                >
                  <MenuItem value="all">Todos los meses</MenuItem>
                  {MONTH_OPTIONS.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Material</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Cantidad</TableCell>
                <TableCell>Precio unitario</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Notas</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedEntries.map((entry) => {
                const material = materialsById[entry.material_id];
                return (
                  <TableRow key={entry.id} hover>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography sx={{ fontWeight: 700 }}>
                          {material?.name ?? entry.material_id}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {material?.code ?? 'Sin código'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>{entry.entry_date}</TableCell>
                    <TableCell>{entry.quantity}</TableCell>
                    <TableCell>{entry.unit_price}</TableCell>
                    <TableCell>{formatDisplayValue(entry.supplier)}</TableCell>
                    <TableCell>{formatDisplayValue(entry.document_ref)}</TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={entry.is_active ? 'Activa' : 'Inactiva'}
                        color={entry.is_active ? 'success' : 'default'}
                        variant={entry.is_active ? 'filled' : 'outlined'}
                      />
                    </TableCell>

                    <TableCell>{formatDisplayValue(entry.notes)}</TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => startEdit(entry)}>
                          Editar
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => void handleDelete(entry.id)}
                        >
                          Eliminar
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

              {!loading && filteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Alert severity="info" sx={{ my: 1 }}>
                      Todavía no hay entradas registradas para este filtro.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>

        {filteredEntries.length > 0 ? (
          <TablePagination
            component="div"
            count={filteredEntries.length}
            page={entriesPage}
            rowsPerPage={entriesRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, newPage) => setEntriesPage(newPage)}
            onRowsPerPageChange={(event) => {
              setEntriesRowsPerPage(Number(event.target.value));
              setEntriesPage(0);
            }}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              borderTop: '1px solid rgba(148,163,184,0.14)',
              color: '#cbd5e1',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                color: '#94a3b8',
              },
              '.MuiTablePagination-actions button': {
                color: '#cbd5e1',
              },
            }}
          />
        ) : null}
      </Paper>
        </>
      )}

      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        fullWidth
        maxWidth={importOnly ? 'xl' : 'lg'}
      >
        <DialogTitle>
          {importOnly
            ? 'Configurar entradas / albaranes'
            : 'Importar entradas de materiales'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {importOnly && (
              <Alert severity="info">
                Comprueba primero la hoja y selecciona con un clic
                la fila que contiene los encabezados. Después podrás revisar
                el mapeo antes de importar.
              </Alert>
            )}

            {importOnly && error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            {importError ? (
              <Alert severity="error">{importError}</Alert>
            ) : null}

            {normalizationResult && !importPreview ? (
              <FileNormalizationReviewPanel
                result={normalizationResult}
                selectedSheetName={
                  normalizationReviewSheetName
                }
                selectedHeaderRowIndex={Math.max(
                  0,
                  Number(
                    normalizationReviewHeaderRowInput,
                  ) - 1,
                )}
                isReviewing={reviewingNormalization}
                isContinuing={
                  previewingImport ||
                  savingNormalizationProfile
                }
                onSheetChange={(sheetName) =>
                  void handleNormalizationSheetChange(
                    sheetName,
                  )
                }
                onHeaderRowChange={(rowIndex) =>
                  setNormalizationReviewHeaderRowInput(
                    String(rowIndex + 1),
                  )
                }
                onContinue={() =>
                  void handleUseNormalization()
                }
              />
            ) : normalizationResult ? (
              <Alert severity="success" variant="outlined">
                Estructura aplicada · Hoja:{' '}
                {normalizationResult.selected_sheet ?? '—'}
                {' · '}Cabecera: fila{' '}
                {normalizationResult.header_row_index + 1}
              </Alert>
            ) : null}

            {importPreview ? (() => {
              const preview = importPreview;

              return (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        md: 'repeat(4, minmax(0, 1fr))',
                      },
                      gap: 1.25,
                    }}
                  >
                    {[
                      { label: 'Filas', value: preview.coverage_summary.total_rows },
                      { label: 'Importables', value: preview.coverage_summary.rows_importable },
                      { label: 'No importables', value: preview.coverage_summary.rows_not_importable },
                      { label: 'Referencias nuevas', value: preview.coverage_summary.unresolved_material_refs_count },
                      { label: 'Fecha inválida', value: preview.coverage_summary.rows_blocked_invalid_date },
                      { label: 'Cantidad inválida', value: preview.coverage_summary.rows_blocked_invalid_quantity },
                      { label: 'Precio inválido', value: preview.coverage_summary.rows_blocked_invalid_unit_price },
                      { label: 'Materiales detectados', value: preview.coverage_summary.detected_material_refs_count },
                    ].map((item) => (
                      <Paper
                        key={item.label}
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: '1px solid rgba(148,163,184,0.16)',
                          bgcolor: 'rgba(15,23,42,0.58)',
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.25rem' }}>
                          {item.value}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant={importStep === 1 ? 'contained' : 'outlined'}
                      onClick={() => setImportStep(1)}
                    >
                      1 · Columnas
                    </Button>
                    <Button
                      size="small"
                      variant={importStep === 2 ? 'contained' : 'outlined'}
                      onClick={() => canContinueImport && setImportStep(2)}
                      disabled={!canContinueImport}
                    >
                      2 · Materiales
                    </Button>
                  </Stack>

                  {importStep === 1 ? (
                    <Stack spacing={2}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Mapeo de columnas
                      </Typography>

                      {preview.missing_required_fields.length > 0 ? (
                        <Alert severity="error">
                          Falta mapear campos obligatorios:{' '}
                          {preview.missing_required_fields.join(', ')}
                        </Alert>
                      ) : null}

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                          gap: 1.25,
                        }}
                      >
                        {directMaterialEntryImportFields.map((field) => (
                          <Box
                            key={field.key}
                            sx={{
                              p: 1.25,
                              border: '1px solid rgba(148,163,184,0.12)',
                              borderRadius: 1,
                              backgroundColor: 'rgba(15,23,42,0.08)',
                            }}
                          >
                            <Stack spacing={0.75}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                  {field.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: field.required ? '#f8fafc' : '#94a3b8',
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {field.required ? 'Obligatorio' : 'Opcional'}
                                </Typography>
                              </Stack>

                              <TextField
                                select
                                size="small"
                                fullWidth
                                value={importMapping[field.key] ?? ''}
                                onChange={(event) =>
                                  handleChangeImportMapping(field.key, event.target.value)
                                }
                              >
                                <MenuItem value="">No mapear</MenuItem>
                                {preview.columns.map((column) => (
                                  <MenuItem key={column} value={column}>
                                    {column}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Stack>
                          </Box>
                        ))}
                      </Box>

                      <Box>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 800, mb: 1 }}>
                          Vista previa
                        </Typography>

                        <Box sx={{ overflowX: 'auto' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Material</TableCell>
                                <TableCell>Fecha</TableCell>
                                <TableCell>Cantidad</TableCell>
                                <TableCell>Precio unitario</TableCell>
                                <TableCell>Proveedor</TableCell>
                                <TableCell>Documento</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {preview.preview_rows.map((row, index) => (
                                <TableRow key={index}>
                                  <TableCell>{formatDisplayValue(row.material)}</TableCell>
                                  <TableCell>{formatDisplayValue(row.entry_date)}</TableCell>
                                  <TableCell>{formatDisplayValue(row.quantity)}</TableCell>
                                  <TableCell>{formatDisplayValue(row.unit_price)}</TableCell>
                                  <TableCell>{formatDisplayValue(row.supplier)}</TableCell>
                                  <TableCell>{formatDisplayValue(row.document_ref)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    </Stack>
                  ) : null}

                  {importStep === 2 ? (
                    <Stack spacing={2}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Resolución de materiales
                      </Typography>

                      {unresolvedMaterialRefs.length === 0 ? (
                        <Alert severity="success">
                          Todas las referencias detectadas ya existen en PROA.
                        </Alert>
                      ) : (
                        <Alert severity="info">
                          Se han detectado {unresolvedMaterialRefs.length} referencias
                          nuevas. PROA las registrará automáticamente al confirmar,
                          siempre que sus filas tengan los datos obligatorios válidos.
                          Puedes vincularlas manualmente a un material existente
                          si alguna referencia corresponde realmente al mismo material.
                        </Alert>
                      )}

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Valor en Excel</TableCell>
                            <TableCell>Filas</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Material PROA</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {preview.detected_material_refs.map((item) => (
                            <TableRow key={item.normalized_value}>
                              <TableCell>{item.source_value}</TableCell>
                              <TableCell>{item.row_count}</TableCell>
                              <TableCell>
                                {item.is_resolved
                                  ? `Existente${item.resolution_source ? ` · ${item.resolution_source}` : ''}`
                                  : 'Nueva · registro automático'}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  value={
                                    materialResolution[item.source_value] ??
                                    item.resolved_material_id ??
                                    ''
                                  }
                                  onChange={(event) =>
                                    handleChangeMaterialResolution(
                                      item.source_value,
                                      event.target.value,
                                    )
                                  }
                                >
                                  <MenuItem value="">
                                    Registrar automáticamente
                                  </MenuItem>
                                  {activeMaterials.map((material) => (
                                    <MenuItem key={material.id} value={material.id}>
                                      {material.code ? `${material.code} · ${material.name}` : material.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Stack>
                  ) : null}
                </>
              );
            })() : !normalizationResult ? (
              <Alert severity="info">
                Selecciona un archivo CSV o XLSX para preparar la importación.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={confirmingImport}>
            Cancelar
          </Button>

          {importPreview && importStep === 1 ? (
            <Button
              variant="contained"
              onClick={() =>
                void handleContinueImportMapping()
              }
              disabled={
                !canContinueImport ||
                previewingImport ||
                savingNormalizationProfile
              }
            >
              {savingNormalizationProfile
                ? 'Guardando...'
                : 'Guardar mapeo y continuar'}
            </Button>
          ) : importPreview ? (
            <Button
              variant="contained"
              onClick={() => void handleConfirmImport()}
              disabled={
                !canContinueImport ||
                confirmingImport
              }
            >
              {confirmingImport ? 'Importando...' : 'Confirmar importación'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={entryDialogOpen} onClose={closeEntryDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {editingId ? 'Editar entrada de material' : 'Nueva entrada de material'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 1 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.9fr 0.8fr 0.8fr' },
                gap: 2,
              }}
            >
              <Autocomplete
                options={activeMaterials}
                value={
                  activeMaterials.find((material) => material.id === form.material_id) ?? null
                }
                onChange={(_, selectedMaterial) =>
                  setForm((prev) => ({
                    ...prev,
                    material_id: selectedMaterial?.id ?? '',
                  }))
                }
                getOptionLabel={(material) =>
                  material.code ? `${material.code} — ${material.name}` : material.name
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) => {
                  const query = state.inputValue.trim().toLowerCase();

                  if (!query) return options;

                  return options.filter((material) =>
                    [
                      material.code,
                      material.name,
                      material.material_type,
                      material.unit_label,
                    ]
                      .filter(Boolean)
                      .some((value) =>
                        String(value).toLowerCase().includes(query),
                      ),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Material *"
                    placeholder="Buscar por código o nombre..."
                    fullWidth
                  />
                )}
              />

              <TextField
                label="Fecha entrada *"
                type="date"
                value={form.entry_date}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, entry_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                label="Cantidad *"
                type="number"
                value={form.quantity}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, quantity: e.target.value }))
                }
                inputProps={{ step: '0.000001' }}
                fullWidth
              />

              <TextField
                label="Precio unitario *"
                type="number"
                value={form.unit_price}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, unit_price: e.target.value }))
                }
                inputProps={{ min: 0, step: '0.000001' }}
                fullWidth
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              <TextField
                label="Proveedor"
                value={form.supplier}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, supplier: e.target.value }))
                }
                fullWidth
              />

              <TextField
                label="Referencia documento"
                value={form.document_ref}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, document_ref: e.target.value }))
                }
                fullWidth
              />

              <TextField
                label="Referencia origen"
                value={form.source_reference}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, source_reference: e.target.value }))
                }
                fullWidth
              />
            </Box>

            <TextField
              label="Notas"
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              fullWidth
            />


            <TextField
              select
              label="Estado"
              value={form.is_active ? 'active' : 'inactive'}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  is_active: e.target.value === 'active',
                }))
              }
              fullWidth
            >
              <MenuItem value="active">Activa</MenuItem>
              <MenuItem value="inactive">Inactiva</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEntryDialog} disabled={saving}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={saving || loading}
          >
            {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear entrada'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}